<template>
    <div>
        <card title="Merge Patients">

            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h1 class="card-title">Merge Patients</h1>

                    <div>
                        <HelpPopup label="Merge Info">
                            <p>
                                All records from source patient will be associated to the target patient
                                and the source patient record will be deleted from the database.
                            </p>
                            <br>
                            <p>
                                The data to be merged is:
                            </p>
                            <ul>
                                <li>Acceptance Records</li>
                                <li>Addresses</li>
                                <li>Allergies</li>
                                <li>Auto-refill Subscriptions</li>
                                <li>Cases</li>
                                <li>Consultations</li>
                                <li>Diagnoses</li>
                                <li>Documents</li>
                                <li>Eligibility Transmissions</li>
                                <li>Emergency Contacts</li>
                                <li>Insurance Requests</li>
                                <li>Insurance Uploads</li>
                                <li>Notes</li>
                                <li>Orders</li>
                                <li>Other Medications</li>
                                <li>Patient Assistance Program</li>
                                <li>Payments</li>
                                <li>Plans</li>
                                <li>Doctor Follow-up Cases</li>
                                <li>Prescriptions</li>
                                <li>Program Eligibility</li>
                                <li>Programs</li>
                                <li>Transfer Requests</li>
                                <li>Tasks</li>
                            </ul>
                        </HelpPopup>
                    </div>
                </div>
            </template>

            <div class="d-flex patient-selector mb-3">
                <div>
                    <SearchComponent clearable v-model="sourcePatient" label='Source' search-u-r-l="/Patient"
                        @input="fetchSourcePatientOrders" :key="sourcePatient.id ?? 0" prepopulate />

                    <PatientRecentlyViewed @change="sourcePatient = $event; hideSourceRecentList = true;"
                        :selected="sourcePatient" :hide-list="hideSourceRecentList" :disable-patient="targetPatient">
                        <template #title>
                            <a href="#" @click.prevent="hideSourceRecentList = !hideSourceRecentList">
                                <i :class="{ 'fa': true, 'fa-chevron-right': true, 'fa-rotate-90': !hideSourceRecentList }"
                                    aria-hidden="true" style="transition: all .2s;"></i>
                                Recently Viewed Patients
                            </a>
                        </template>
                    </PatientRecentlyViewed>
                </div>

                <span>
                    <b-button size="sm" class="swap" @click="swap" variant="outline-default" v-b-tooltip.hover
                        title="Swap Source and Target">
                        <i class="fa fa-exchange" />
                    </b-button>
                </span>

                <div>
                    <SearchComponent clearable v-model="targetPatient" label='Target' search-u-r-l="/Patient"
                        @input="loadTargetPatient" :key="targetPatient.id ?? 0" prepopulate />

                    <PatientRecentlyViewed @change="targetPatient = $event; hideTargetRecentList = true;"
                        :selected="targetPatient" :hide-list="hideTargetRecentList" :disable-patient="sourcePatient">
                        <template #title>
                            <a href="#" @click.prevent="hideTargetRecentList = !hideTargetRecentList">
                                <i :class="{ 'fa': true, 'fa-chevron-right': true, 'fa-rotate-90': !hideTargetRecentList }"
                                    aria-hidden="true" style="transition: all .2s;"></i>
                                Recently Viewed Patients
                            </a>
                        </template>
                    </PatientRecentlyViewed>
                </div>
            </div>

            <b-row>
                <b-col v-if="!isMerging">
                    <b-row>
                        <b-col>
                            <b-button v-if="userCanMerge" @click="confirmDialog" variant="success"
                                :disabled="disableSubmit">
                                Merge
                            </b-button>
                            <b-button v-else @click="addQueue" variant="outline-primary">
                                Add to Merge Queue
                            </b-button>
                        </b-col>
                        <b-col class="text-right">
                            <b-button href="/maintenance/merge-patients-queue" variant="default">
                                View Patient Merge Queue
                            </b-button>
                        </b-col>
                    </b-row>
                </b-col>
                <b-col v-else>
                    <div>
                        <b-spinner label="Spinning" class="mr-2"></b-spinner>
                        <strong>Merging...</strong>
                    </div>
                </b-col>
            </b-row>
        </card>

        <!-- Active Orders Alert -->
        <b-row v-if="sourcePatientHaveActiveOrders">
            <b-col>
                <b-alert variant="danger" v-model="sourcePatientHaveActiveOrders">
                    <span><b>Source patient has {{ sourcePatientOrders.length }} {{ sourcePatientOrders.length > 1 ?
                        "orders" :
                            "order" }} still active</b>,
                        all its orders have to be shipped or cancelled / failed to be able to merge it into target
                        patient.
                    </span>
                    <span>See its patient profile for more information.</span>
                </b-alert>
            </b-col>
        </b-row>

        <!-- Demographics -->
        <div class="d-flex preview">
            <div>
                <card class="mb-0">
                    <b-button variant="primary"
                        :to="{ name: 'PatientDashboard', params: { id: (sourcePatient && sourcePatient.id) } }"
                        v-if="sourcePatient.id && !loadingSourcePatient">
                        <span>Go To Profile</span> <i class="fas fa-user"></i>
                    </b-button>
                    <div v-if="loadingSourcePatient">
                        <b-spinner label="Spinning" class="mr-2"></b-spinner>
                        <strong>Loading...</strong>
                    </div>
                    <PatientDemographicsComponent v-if="sourcePatient.id && !loadingSourcePatient"
                        v-model="sourcePatient">
                    </PatientDemographicsComponent>
                    <span v-else>Select a <b>source</b> patient</span>
                </card>
            </div>
            <i class="fas fa-arrow-circle-right" />
            <div>
                <card class="mb-0">
                    <b-button variant="primary"
                        :to="{ name: 'PatientDashboard', params: { id: (targetPatient && targetPatient.id) } }"
                        v-if="targetPatient.id && !loadingTargetPatient">
                        <span>Go To Profile</span> <i class="fas fa-user"></i>
                    </b-button>
                    <div v-if="loadingTargetPatient">
                        <b-spinner label="Spinning" class="mr-2"></b-spinner>
                        <strong>Loading...</strong>
                    </div>
                    <PatientDemographicsComponent v-if="targetPatient.id && !loadingTargetPatient"
                        v-model="targetPatient">
                    </PatientDemographicsComponent>
                    <span v-else>Select a <b>target</b> patient</span>
                </card>
            </div>
        </div>
    </div>
</template>

<script lang="ts">

import { Component, Vue } from 'vue-property-decorator';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Patient } from '@/models/Patient';
import { Order } from '@/models/Order';
import SearchComponent from '@/components/SearchComponent.vue';
import PatientDemographicsComponent from '@/components/Patient/PatientDemographicsComponent.vue';
import HelpPopup from "@/components/HelpPopup.vue";
import PatientRecentlyViewed from "@/components/Patient/PatientRecentlyViewed.vue";

@Component({
    name: "MergePatientsPage",
    components: { PatientDemographicsComponent, SearchComponent, HelpPopup, PatientRecentlyViewed }
})
export default class MergePatientsPage extends Vue {
    private sourcePatient = new Patient();
    private targetPatient = new Patient();
    private sourcePatientOrders = new Array<Order>();
    private hideSourceRecentList = true;
    private hideTargetRecentList = true;

    get disableSubmit(): boolean {
        return !this.sourcePatient?.id || !this.targetPatient?.id || this.sourcePatientHaveActiveOrders;
    }

    get sourcePatientHaveActiveOrders(): boolean {
        return !!this.sourcePatientOrders?.length;
    }

    get userCanMerge() {
        return this.$permissions.some(p => p.permissionCodeName.toLowerCase() === "Maintenance/Merge Patients".toLowerCase());
    }

    mounted() {
        const routeSourceId = this.$route.params.sourcePatientId;
        const routeTargetId = this.$route.params.targetPatientId;

        if (routeSourceId) {
            this.fetchSourcePatientOrders(Object.assign(new Patient(), { id: routeSourceId }));
        }

        if (routeTargetId) {
            this.loadTargetPatient(Object.assign(new Patient(), { id: routeTargetId }));
        }
    }

    updated() {
        console.log('sourcePatientHaveActiveOrders', this.sourcePatientHaveActiveOrders);
        console.log('sourcePatientOrders', this.sourcePatientOrders);
    }

    loadPatientPromise(patientID: number | string): Promise<Patient | string> {
        return new Promise<Patient | string>((resolve, reject) => {
            this.$http.get<Patient>(`/Patient/${patientID}`)
                .then(res => {
                    if (res.data?.id) resolve(res.data);
                    else reject('Patient Not Found');
                })
                .catch(err => {
                    let errorMsg = "Error loading Source Patient Orders";
                    console.error(errorMsg, { err, response: err?.response });
                    this.$notification(NotificationOptions.error(err));
                    reject(errorMsg);
                })
        })
    }

    private loadingSourcePatient = false;
    fetchSourcePatientOrders(sourcePatient: Patient) {
        if (!sourcePatient?.id) return;

        this.sourcePatientOrders = new Array<Order>();
        this.loadingSourcePatient = true;
        this.loadPatientPromise(sourcePatient.id)
            .then(res => {
                if ((res as Patient)?.id) {
                    this.sourcePatient = new Patient(res as Patient);

                    this.$http.get<Array<Order>>(`/Patient/${sourcePatient.id}/orders/`)
                        .then(res => {
                            if (res.data?.length)
                                this.sourcePatientOrders = res.data.map(o => new Order(o));
                        })
                        .catch(err => {
                            console.error("Error loading Source Patient Orders", { err, response: err?.response });
                            this.$notification(NotificationOptions.error(err));
                        })
                        .finally(() => {
                            this.loadingSourcePatient = false;
                        })
                }
            })
    }

    private loadingTargetPatient = false;
    loadTargetPatient(targetPatient: Patient) {
        if (!targetPatient) return;

        this.loadingTargetPatient = true;
        this.loadPatientPromise(targetPatient.id)
            .then(res => {
                if ((res as Patient)?.id) this.targetPatient = new Patient(res as Patient);
            })
            .finally(() => {
                this.loadingTargetPatient = false;
            })
    }

    confirmDialog() {
        this.$bvModal.msgBoxConfirm("This action is not reversible. Are you sure?", {
            title: 'Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
        })
            .then(value => {
                if (!value) return;
                this.submitMerge();
            })
            .catch(err => {
                console.error("Error caught on Clear button.", err);
            });
    }

    private isMerging = false;

    submitMerge() {
        if (!this.sourcePatient?.id || !this.targetPatient?.id) return;

        this.isMerging = true;
        let errorMsg = 'Error while merging patients.'
        this.$http.post<Patient>(`/patient/merge-patients/${this.sourcePatient.id}/${this.targetPatient.id}`)
            .then(res => {
                if (res.data?.id) {
                    this.targetPatient = new Patient(res.data);
                    this.sourcePatient = new Patient();
                    this.$notification(NotificationOptions.notificationOptionsPreset("Source patient was successfully merged into target patient and removed from database.", NotificationOptions.NotificationTypes.success));
                } else {
                    this.$notification(NotificationOptions.notificationOptionsPreset(errorMsg, NotificationOptions.NotificationTypes.danger));
                    console.error(errorMsg, { res, response: res?.data });
                }
            })
            .catch(err => {
                this.$notification(NotificationOptions.errorSaveNotificationPreset("Patient merge", err));
                console.error(err);
            })
            .finally(() => this.isMerging = false)
    }

    swap() {
        [this.sourcePatient, this.targetPatient] = [this.targetPatient, this.sourcePatient];
    }

    async addQueue() {

        try {

            if (!this.sourcePatient?.id || !this.targetPatient?.id) {
                this.$notification(NotificationOptions.error("Must select a source and target patient for the merge request"));
                return;
            }

            if (this.sourcePatient.id === this.targetPatient.id) {
                this.$notification(NotificationOptions.error("Source and target patient can't be the same record"));
                return;
            }

            const request = `/patient/mergequeue/${this.sourcePatient.id}/${this.targetPatient.id}`;
            await this.$http.post(request);

            this.$notification(NotificationOptions.notificationOptionsPreset("Patient merge request added", NotificationOptions.NotificationTypes.success));

            this.sourcePatient = new Patient();
            this.targetPatient = new Patient();

        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }

    }
}
</script>

<style scoped lang="scss">
.btn.swap {
    margin-top: 28px;
    font-size: 1.4em;
    border-radius: 30px;
}

.patient-selector {
    gap: 12px;

    >div {
        width: 50%;
    }
}

.preview {
    gap: 15px;
    align-items: center;

    >i {
        font-size: 3em;
        margin: 0 -25px;
        z-index: 100;
    }

    >div {
        width: 50%;
    }
}
</style>
